// Core
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// Interfaces
import { finalize, tap } from 'rxjs/operators';

import { ShipmentStatusService } from '@app/core/shipment/shipment-status.service';
import { PurchaseOrderStatusInterface } from '@app/interfaces/purchase-order-status.model';

import { OrderSupplierPurchaseService } from '../shared/services/api/order/order-supplier-purchase.service';

import {
  IPagination,
  IPaginationResponse,
} from './../shared/interfaces/paginator.interface';
import { OrderSupplierPurchaseShipmentService } from './../shared/services/api/order/order-supplier-purchase-shipment.service';
import {
  IPurchaseOrderShipment,
  IPurchaseOrderState,
} from './interfaces/purchase-order.interface';

// Services

import { PurchaseOrderStateService } from './purchase-order-state.service';

// Utilities

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderFacadeService {
  constructor(
    private purchaseOrderStateService :PurchaseOrderStateService,
    private shipmentStatusService :ShipmentStatusService,
    private orderSupplierPurchaseService :OrderSupplierPurchaseService,
    private orderSupplierPurchaseShipmentService :OrderSupplierPurchaseShipmentService,
  ) {}

  getPurchaseOrders(
    params :{ [param :string] :string | string[] } = {},
  ) :Observable<IPaginationResponse> {
    this.purchaseOrderStateService.updateState({ loadingPurchaseOrders: true });

    return this.orderSupplierPurchaseService.get(params).pipe(
      finalize(() =>
        this.purchaseOrderStateService.updateState({
          loadingPurchaseOrders: false,
        }),
      ),
      tap((response) => {
        this.purchaseOrderStateService.updateState({
          purchaseOrders: response.data,
        });
      }),
    );
  }

  getShipments(
    params :{ [param :string] :string | string[] } = {},
  ) :Observable<IPaginationResponse> {
    this.purchaseOrderStateService.updateState({ loadingShipments: true });

    return this.orderSupplierPurchaseShipmentService.get(params).pipe(
      finalize(() =>
        this.purchaseOrderStateService.updateState({ loadingShipments: false }),
      ),
      tap((response) => {
        const { data, meta } = response;
        this.purchaseOrderStateService.updateState({
          meta,
          shipments: data,
        });
      }),
    );
  }

  getShipmentById(data :{
    id :number;
    params :{ [param :string] :string | string[] };
  }) :Observable<IPurchaseOrderShipment> {
    const { id, params = {} } = data;
    this.purchaseOrderStateService.updateState({
      currentShipment: null,
      loadingCurrentShipment: true,
    });

    return this.orderSupplierPurchaseShipmentService.getById({ id, params }).pipe(
      finalize(() =>
        this.purchaseOrderStateService.updateState({
          loadingCurrentShipment: false,
        }),
      ),
      tap((response) => {
        this.purchaseOrderStateService.updateState({ currentShipment: response });
      }),
    );
  }

  getShipmentStatus() :Observable<IPaginationResponse> {
    return this.shipmentStatusService.getV2({}).pipe(
      tap((response) => {
        this.purchaseOrderStateService.updateState({
          shipmentStatus: response.data,
        });
      }),
    );
  }

  createShipment(payload :any) :Observable<any> {
    this.purchaseOrderStateService.updateState({ submitting: true });

    return this.orderSupplierPurchaseShipmentService.create(payload).pipe(
      finalize(() =>
        this.purchaseOrderStateService.updateState({ submitting: false }),
      ),
      tap(() => {
        this.getShipments(this.currentShipmentQueryParams).subscribe();
      }),
    );
  }

  updateShipment(id :number, payload :any) :Observable<any> {
    this.purchaseOrderStateService.updateState({ submitting: true });

    return this.orderSupplierPurchaseShipmentService.update({ id, payload }).pipe(
      finalize(() =>
        this.purchaseOrderStateService.updateState({ submitting: false }),
      ),
      tap(() => {
        this.getShipments(this.currentShipmentQueryParams).subscribe();
      }),
    );
  }

  deleteShipment(id :number) :Observable<any> {
    this.purchaseOrderStateService.updateState({ loadingShipments: true });

    return this.orderSupplierPurchaseShipmentService.delete(id).pipe(
      finalize(() =>
        this.purchaseOrderStateService.updateState({ loadingShipments: false }),
      ),
      tap(() => {
        this.getShipments(this.currentShipmentQueryParams).subscribe();
      }),
    );
  }

  setCurrentQueryParams(params :{ [param :string] :string | string[] }) :void {
    this.purchaseOrderStateService.updateState({ currentQueryParams: params });
    this.getShipments(params).subscribe();
  }

  setState(newState :Partial<IPurchaseOrderState>) :void {
    this.purchaseOrderStateService.updateState({
      ...this.purchaseOrderStateService.state,
      ...newState,
    });
  }

  get purchaseOrders$() :Observable<any[]> {
    return this.purchaseOrderStateService.purchaseOrders$;
  }

  get shipments$() :Observable<IPurchaseOrderShipment[]> {
    return this.purchaseOrderStateService.shipments$;
  }

  get shipmentStatus$() :Observable<PurchaseOrderStatusInterface[]> {
    return this.purchaseOrderStateService.shipmentStatus$;
  }

  get loadingPurchaseOrders$() :Observable<boolean> {
    return this.purchaseOrderStateService.loadingPurchaseOrders$;
  }

  get loadingShipments$() :Observable<boolean> {
    return this.purchaseOrderStateService.loadingShipments$;
  }

  get submitting$() :Observable<boolean> {
    return this.purchaseOrderStateService.submitting$;
  }

  get loadingCurrentShipment$() :Observable<boolean> {
    return this.purchaseOrderStateService.loadingCurrentShipment$;
  }

  get currentShipment$() :Observable<IPurchaseOrderShipment> {
    return this.purchaseOrderStateService.currentShipment$;
  }

  // DIRECT VALUE ACCESSORS
  get currentShipmentQueryParams() :any {
    return this.purchaseOrderStateService.state.currentQueryParams;
  }

  get pagination() :IPagination {
    return this.purchaseOrderStateService.state.meta;
  }
}
